import { useEffect, useState } from 'react';

import { MtxGrid, MtxIcon } from '@meilleurtaux/mtp-uikit-react';
import { useQueryClient } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ChartDataPoint, getGetApiPlacementImmobilierQueryKey, useGetApiPlacementImmobilier } from '@/api';
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb';
import ChipsRadioSmall from '@/components/chipsRadioSmall/chipsRadioSmall';
import ColumnChart from '@/components/ColumnChart/ColumnChart';
import ContractDetailsHeader from '@/components/ContractDetailsHeader/ContractDetailsHeader';
import { ContractDocuments } from '@/components/ContractDocuments/ContractDocuments';
import DemoTag from '@/components/DemoTag/DemoTag';
import LineChart from '@/components/LineChart/LineChart';
import { dataButtonFiltre } from '@/components/LineChart/lineChartButtons';
import { Loader } from '@/components/Loader/loader';
import ModaleDefaut from '@/components/ModaleDefaut/ModaleDefaut';
import Tabs from '@/components/Tabs/Tabs';
import { ROUTE } from '@/constants/route';
import { REAL_ESTATE_CAPITAL_TYPE } from '@/enums/realEstate';
import { TIMESPAN } from '@/enums/timespan';
import { useDemoContext } from '@/hooks/DemoContext';
import { useAuth } from '@/hooks/useAuth';
import useCancelModal from '@/hooks/useCancelModal';
import { useFilteredDocuments } from '@/hooks/useFilteredDocuments';
import { TRACKING_INSTANCE } from '@/hooks/useTracking/useTrackingInit/useTrackingInit';
import ContentModalRenonciation from '@/modals/ContentModalRenonciation/ContentModalRenonciation';
import { BreadcrumbItem } from '@/services/types/Breadcrumb';

import styles from './styles.module.css';

export const ImmoDetails: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { isDemo } = useDemoContext();
  const [lineChartTimespan, setLineChartTimespan] = useState<TIMESPAN>(TIMESPAN.allTime);
  const [showModalTauxDistrib, setShowModalTauxDistrib] = useState(false);
  const [showModalTri, setShowModalTri] = useState(false);
  const [showModalIconInfo, setShowModalIconInfo] = useState(false);
  const [showModalIconInfo2, setShowModalIconInfo2] = useState(false);
  const [showModalFixedCapital, setShowModalFixedCapital] = useState(false);
  const { showCancelModal, openCancelModal, closeCancelModal } = useCancelModal();

  const { id } = useParams<{ id: string }>();
  const { data, isLoading, isError } = useGetApiPlacementImmobilier(id ?? '');
  const cancelablePeriodDays = data?.cancelablePeriod == null ? 14 : Number(data.cancelablePeriod);

  const openModal = (modal: string) => {
    document.body.classList.add('modal-open');

    if (modal === 'showModalTauxDistrib') {
      setShowModalTauxDistrib(true);
    } else if (modal === 'showModalTri') {
      setShowModalTri(true);
    } else if (modal === 'showModalIconInfo') {
      setShowModalIconInfo(true);
    } else if (modal === 'showModalIconInfo2') {
      setShowModalIconInfo2(true);
    } else if (modal === 'showModalFixedCapital') {
      setShowModalFixedCapital(true);
    }
  };

  const closeModal = () => {
    document.body.classList.remove('modal-open');
    setShowModalTauxDistrib(false);
    setShowModalTri(false);
    setShowModalIconInfo(false);
    setShowModalIconInfo2(false);
    setShowModalFixedCapital(false);
  };

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: 'Mes contrats', path: ROUTE.CONTRATS },
    { label: data?.nomProduit, path: ROUTE.IMMOBILIER },
  ];

  useEffect(() => {
    if (data) {
      TRACKING_INSTANCE.setTcVars({
        page_nom: `mes contrats - ${data?.nomProduit}`,
        page_template: 'espace client',
        user_id: user?.id,
        user_client: user?.type === 'Client',
        user_connexion: user?.success,
        user_statut_contrat: sessionStorage.getItem('user_statut_contrat'),
      });
      void TRACKING_INSTANCE.triggerEvent('page', undefined, {
        event_id: 'page',
        event_categorie: 'affichage espace client',
        event_nom: `mes contrats - ${data?.nomProduit} - contrat`,
        event_page: `mes contrats - ${data?.nomProduit} - contrat`,
      });
    }
  }, [data, user]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const { exPostDocuments, regularDocuments } = useFilteredDocuments({
    documents: data?.documents,
  });

  const valorisationEncours = data?.valorisation?.dateEncours
    ? data.valorisation.dateEncours.split('-').reverse().join('/')
    : '--/--/----';

  const subscriptionDate = data?.dateSouscription ? data.dateSouscription.split('-').reverse().join('/') : null;

  if (isLoading)
    return (
      <div>
        {' '}
        <Loader />
      </div>
    );
  if (isError) navigate(ROUTE.ERREUR, { state: { goTo: ROUTE.CONTRATS } });

  return (
    <div className={styles.contractDetailsContainer}>
      {showModalTauxDistrib && (
        <ModaleDefaut
          title={t('modals.realEstatePayoutRateInfoModal.titleText')}
          content={
            <p>
              <Trans
                i18nKey="modals.realEstatePayoutRateInfoModal.descriptionText"
                components={{
                  b: <strong className={styles.bolderText} />,
                }}
              />
            </p>
          }
          btnLabel="J'ai compris"
          btnVariant="primary"
          btnSize="large"
          onClickBtn={closeModal}
          btnFullwidth={false}
          onClickClose={closeModal}
          mobileStyle="stick-to-bottom"
          size="large"
        />
      )}
      {showModalTri && (
        <ModaleDefaut
          title={t('modals.realEstateRoiInfoModal.subTitle')}
          content={
            <p>
              <Trans
                i18nKey="modals.realEstateRoiInfoModal.descriptionText"
                components={{
                  b: <strong className={styles.bolderText} />,
                }}
              />
            </p>
          }
          btnLabel="J'ai compris"
          btnVariant="primary"
          btnSize="large"
          onClickBtn={closeModal}
          btnFullwidth={false}
          onClickClose={closeModal}
          mobileStyle="stick-to-bottom"
          size="large"
        />
      )}
      {showModalIconInfo && (
        <ModaleDefaut
          title={t('modals.realEstateUsufructInfoModal.titleText')}
          content={<p>{t('modals.realEstateUsufructInfoModal.descriptionText')}</p>}
          btnLabel="J'ai compris"
          btnVariant="primary"
          btnSize="large"
          onClickBtn={closeModal}
          btnFullwidth={false}
          onClickClose={closeModal}
          mobileStyle="stick-to-bottom"
          size="large"
        />
      )}
      {showModalIconInfo2 && (
        <ModaleDefaut
          title={t('modals.realEstateBareOwnershipInfoModal.titleText')}
          content={<p>{t('modals.realEstateBareOwnershipInfoModal.descriptionText')}</p>}
          btnLabel="J'ai compris"
          btnVariant="primary"
          btnSize="large"
          onClickBtn={closeModal}
          btnFullwidth={false}
          onClickClose={closeModal}
          mobileStyle="defaut"
          size="large"
        />
      )}
      {showModalFixedCapital && (
        <ModaleDefaut
          content={<p>{t('modals.fixedCapitalInfoModal.descriptionText')}</p>}
          btnLabel="J'ai compris"
          btnVariant="primary"
          btnSize="large"
          onClickBtn={closeModal}
          btnFullwidth={false}
          onClickClose={closeModal}
          mobileStyle="defaut"
          size="large"
        />
      )}

      {showCancelModal && (
        <ContentModalRenonciation
          nomProduit={data?.nomProduit ?? ''}
          placementId={data?.placementId ?? ''}
          delayDays={cancelablePeriodDays}
          onClose={closeCancelModal}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: getGetApiPlacementImmobilierQueryKey(id ?? '') });
            closeCancelModal();
          }}
        />
      )}

      <div>
        <Breadcrumb items={breadcrumbItems} />
      </div>
      <div className={styles.contractDetailImmoHeader}>
        <div>
          <ContractDetailsHeader
            nomProduit={data?.nomProduit}
            isCancelable={data?.isCancelable}
            cancelablePeriod={cancelablePeriodDays}
            manager={data?.gestionnaire}
            openCancelModal={openCancelModal}
          />

          <div className={styles.realEstateValuationAmountDate}>
            <div className={styles.contractDetailsValuationAmountDate}>
              <div className={styles.realEstateValuation}>
                <span className={styles.valuation}>
                  {t('format.currencyFlexible', { value: data?.valorisation?.encours ?? 0 })}
                </span>
                {data?.modeAquisition !== 'Pleine Propriété' && (
                  <button
                    className={styles.contractDetailsValuationInfo}
                    onClick={() => {
                      if (data?.modeAquisition === 'Usufruit') {
                        openModal('showModalIconInfo');
                      } else {
                        openModal('showModalIconInfo2');
                      }
                      void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
                        event_id: 'clic',
                        event_categorie: 'clic',
                        event_metier: 'espace client placement',
                        event_nom: 'info immo',
                        event_detail_1: 'picto',
                      });
                    }}
                  >
                    <i className="totem-icon totem-icon-info" />
                  </button>
                )}
                {isDemo && <DemoTag />}
              </div>

              <p className={styles.contractDetailsValuationDate}>
                {t('realEstate.description')} {valorisationEncours}
              </p>
            </div>
          </div>
        </div>
      </div>

      <Tabs
        tabs={[
          {
            label: 'Contrat',
            disabled: false,
            onClick: () => {
              void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
                event_id: 'clic',
                event_categorie: 'clic',
                event_metier: 'espace client placement',
                event_nom: 'contrat',
                event_detail_1: 'onglet',
              });
            },
            content: (
              <div className={styles.contractDetailsSection}>
                <h2 className={styles.contractDetailsSectionTitle}>
                  {t('realEstate.title')}
                  {isDemo && <DemoTag />}
                </h2>

                <MtxGrid row>
                  <MtxGrid col={7} colXs={12} colSm={12} colMd={12} colLg={7}>
                    <table className={styles.contractDetailsTable}>
                      <tbody className={styles.contractDetailsList}>
                        {data?.gestionnaire !== null && (
                          <tr className={styles.contractDetailsLine}>
                            <td className={styles.contractDetailsTitle}>
                              <p>{t('realEstate.label')}</p>
                            </td>
                            <td className={styles.contractDetailsValue}>
                              <p>{data?.gestionnaire}</p>
                            </td>
                          </tr>
                        )}

                        {subscriptionDate && (
                          <tr className={styles.contractDetailsLine}>
                            <td className={styles.contractDetailsTitle}>
                              <p>{t('realEstate.subscriptionDateLabel')}</p>{' '}
                            </td>
                            <td className={styles.contractDetailsValue}>
                              <p>{subscriptionDate}</p>
                            </td>
                          </tr>
                        )}
                        {data?.type !== null && (
                          <tr className={styles.contractDetailsLine}>
                            <td className={styles.contractDetailsTitle}>
                              <p>{t('realEstate.typeLabel')}</p>
                            </td>
                            <td className={styles.contractDetailsValue}>
                              <p>{data?.type}</p>
                            </td>
                          </tr>
                        )}
                        {data?.modeAquisition !== null && (
                          <tr className={styles.contractDetailsLine}>
                            <td className={styles.contractDetailsTitle}>
                              <p>{t('realEstate.acquisitionModeLabel')}</p>{' '}
                            </td>
                            <td className={styles.contractDetailsValue}>
                              <p>{data?.modeAquisition}</p>
                            </td>
                          </tr>
                        )}
                        {data?.nombrePart !== null && (
                          <tr className={styles.contractDetailsLine}>
                            <td className={styles.contractDetailsTitle}>
                              {' '}
                              <p>{t('realEstate.numberOfSharesLabel')}</p>
                            </td>
                            <td className={styles.contractDetailsValue}>
                              <p>{data?.nombrePart}</p>
                            </td>
                          </tr>
                        )}
                        {typeof data?.valeurOrigine === 'number' && (
                          <tr className={styles.contractDetailsLine}>
                            <td className={styles.contractDetailsTitle}>
                              <p>{t('realEstate.purchasePriceLabel')}</p>
                            </td>
                            <td className={styles.contractDetailsValue}>
                              <p>{t('format.currencyFlexible', { value: data?.valeurOrigine })}</p>
                            </td>
                          </tr>
                        )}
                        {typeof data?.valeurRetrait === 'number' && (
                          <tr className={styles.contractDetailsLine}>
                            <td className={styles.contractDetailsTitle}>
                              <p>{t('realEstate.resalePriceLabel')}</p>
                              {data?.typeCapital === REAL_ESTATE_CAPITAL_TYPE.FIXED_CAPITAL && (
                                <button
                                  className={styles.contractDetailsMoreInfo}
                                  onClick={() => {
                                    openModal('showModalFixedCapital');
                                  }}
                                >
                                  <MtxIcon name="info" size={24} />
                                </button>
                              )}
                            </td>
                            <td className={styles.contractDetailsValue}>
                              <p>
                                {' '}
                                {data?.typeCapital === REAL_ESTATE_CAPITAL_TYPE.FIXED_CAPITAL
                                  ? t('realEstate.infoUnavailable')
                                  : t('format.currencyFlexible', { value: data?.valeurRetrait })}
                              </p>
                            </td>
                          </tr>
                        )}
                        {typeof data?.versementInitial === 'number' && (
                          <tr className={styles.contractDetailsLine}>
                            <td className={styles.contractDetailsTitle}>
                              <p>{t('realEstate.initialDepositLabel')}</p>{' '}
                            </td>
                            <td className={styles.contractDetailsValue}>
                              <p>{t('format.currencyFlexible', { value: data?.versementInitial })}</p>
                            </td>
                          </tr>
                        )}
                        {data?.rapportGestion && (
                          <tr className={styles.contractDetailsLine}>
                            <td className={styles.contractDetailsTitle}>
                              <p>{t('realEstate.lastManagementReportLabel')}</p>{' '}
                            </td>
                            <td className={styles.contractDetailsValue}>
                              <a
                                className={styles.linkRapportDocument}
                                href={data?.rapportGestion}
                                target="_blank"
                                onClick={() => {
                                  void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
                                    event_id: 'clic',
                                    event_categorie: 'clic',
                                    event_metier: 'espace client placement',
                                    event_nom: 'telecharger dernier rapport',
                                    event_detail_1: 'picto',
                                  });
                                }}
                                rel="noreferrer noopener"
                              >
                                <i className="totem-icon totem-icon-eye" />
                              </a>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </MtxGrid>
                </MtxGrid>
              </div>
            ),
          },
          {
            label: 'Performance',
            disabled: false,
            onClick: () => {
              void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
                event_id: 'clic',
                event_categorie: 'clic',
                event_metier: 'espace client placement',
                event_nom: 'performance',
                event_detail_1: 'onglet',
              });
            },
            content: (
              <div className={styles.contractDetailBody}>
                {data?.TRI?.length ? (
                  <div className={styles.contractDetailSection}>
                    <h2 className={`${styles.detailContractSectionTitle} ${styles.detailContractSectionTitleInfo}`}>
                      {t('realEstate.roiTitle')}
                      <button
                        className={styles.contractDetailMoreInfo}
                        onClick={() => {
                          openModal('showModalTri');
                          void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
                            event_id: 'clic',
                            event_categorie: 'clic',
                            event_metier: 'espace client placement',
                            event_nom: 'info tri',
                            event_detail_1: 'picto',
                          });
                        }}
                      >
                        <i className="totem-icon totem-icon-info" />
                      </button>
                    </h2>

                    <MtxGrid row>
                      <MtxGrid col={7} colXs={12} colSm={12} colMd={12} colLg={7}>
                        <table className={styles.contractDetailsTable}>
                          <tbody className={styles.contractDetailsList}>
                            {data?.TRI.map((t) => {
                              return (
                                <tr key={`${t.nbAnnees}-${t.pourcentage}`} className={styles.contractDetailsLine}>
                                  <td className={styles.contractDetailsTitle}>
                                    <p>SUR {t.nbAnnees} ANS</p>
                                  </td>
                                  <td className={styles.contractDetailsValue}>
                                    <p> {t.pourcentage} %</p>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </MtxGrid>
                    </MtxGrid>
                  </div>
                ) : null}

                {data?.tauxDistribution && (
                  <div className={styles.contractDetailSection}>
                    <h2 className={`${styles.detailContractSectionTitle} ${styles.detailContractSectionTitleInfo}`}>
                      {t('realEstate.distributionRateTitle')}
                      <button
                        className={styles.contractDetailMoreInfo}
                        onClick={() => {
                          openModal('showModalTauxDistrib');
                          void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
                            event_id: 'clic',
                            event_categorie: 'clic',
                            event_metier: 'espace client placement',
                            event_nom: 'info taux de distribution',
                            event_detail_1: 'picto',
                          });
                        }}
                      >
                        <i className="totem-icon totem-icon-info" />
                      </button>
                    </h2>

                    <MtxGrid row>
                      <MtxGrid col={7} colXs={12} colSm={12} colMd={12} colLg={7} colXl={7}>
                        <ColumnChart donnees={data?.tauxDistribution ?? []} />
                      </MtxGrid>
                    </MtxGrid>
                  </div>
                )}

                {data?.typeCapital !== REAL_ESTATE_CAPITAL_TYPE.FIXED_CAPITAL && (
                  <div className={styles.contractDetailSection}>
                    <h2 className={styles.detailContractSectionTitle}>{t('realEstate.resalePriceEvolutionTitle')}</h2>

                    <MtxGrid col={7} colXs={12} colSm={12} colMd={12} colLg={7}>
                      <LineChart
                        timespan={lineChartTimespan}
                        donnees={(data?.evoPrixRevente as ChartDataPoint[]) ?? []}
                      />
                    </MtxGrid>
                    <MtxGrid col={7} colXs={12} colSm={12} colMd={12} colLg={7}>
                      <div className={styles.filterButtonContent}>
                        {dataButtonFiltre.map((value, index, buttonsFiltre) => (
                          <ChipsRadioSmall
                            key={value.valueImmo}
                            label={value.textImmo}
                            name="lineChartButtons"
                            value={value.valueImmo}
                            defaultCheck={index + 1 === buttonsFiltre.length}
                            onClick={() => {
                              setLineChartTimespan(value.valueImmo);
                              void TRACKING_INSTANCE.triggerEvent('clic', undefined, value.trackingImmo);
                            }}
                          />
                        ))}
                      </div>
                    </MtxGrid>
                  </div>
                )}
              </div>
            ),
          },
          {
            label: 'Documents',
            disabled: data?.documents === null,
            onClick: () => {
              void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
                event_id: 'clic',
                event_categorie: 'clic',
                event_metier: 'espace client placement',
                event_nom: 'documents',
                event_detail_1: 'onglet',
              });
            },
            content: (
              <div className={styles.contractDetailBody}>
                <div className={styles.contractDetailSection}>
                  <div className={styles.contractDetailSectionContent}>
                    <h2 className={styles.detailContractSectionTitle}>
                      {t('documents.regularReport', { count: regularDocuments.length })}
                    </h2>

                    <MtxGrid row>
                      <MtxGrid col={7} colXs={12} colSm={12} colMd={12} colLg={7}>
                        <ContractDocuments documentsInfos={regularDocuments} />
                      </MtxGrid>
                    </MtxGrid>
                  </div>

                  {exPostDocuments.length > 0 && (
                    <div className={styles.contractDetailSectionContent}>
                      <h2 className={styles.detailContractSectionTitle}>
                        {t('documents.exPostReport', { count: exPostDocuments.length })}
                      </h2>
                      <MtxGrid row>
                        <MtxGrid col={7} colXs={12} colSm={12} colMd={12} colLg={7}>
                          <ContractDocuments documentsInfos={exPostDocuments} />
                        </MtxGrid>
                      </MtxGrid>
                    </div>
                  )}
                </div>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};
